/**
 * Request Queue Manager
 * Prevents API overload by limiting concurrent requests
 * Browser typically allows 6 concurrent requests per domain
 */

class RequestQueue {
  private queue: Array<() => Promise<any>> = [];
  private activeRequests = 0;
  private readonly maxConcurrent = 6;

  /**
   * Add request to queue
   * Executes immediately if under limit, otherwise queues
   */
  async add<T>(requestFn: () => Promise<T>): Promise<T> {
    if (this.activeRequests < this.maxConcurrent) {
      return this.execute(requestFn);
    }

    // Queue the request
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await this.execute(requestFn);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  /**
   * Execute a request and manage queue
   */
  private async execute<T>(requestFn: () => Promise<T>): Promise<T> {
    this.activeRequests++;
    
    try {
      const result = await requestFn();
      return result;
    } finally {
      this.activeRequests--;
      this.processNext();
    }
  }

  /**
   * Process next item in queue if available
   */
  private processNext(): void {
    if (this.queue.length === 0) return;
    if (this.activeRequests >= this.maxConcurrent) return;

    const nextRequest = this.queue.shift();
    if (nextRequest) {
      nextRequest();
    }
  }

  /**
   * Get queue statistics (useful for debugging)
   */
  getStats() {
    return {
      active: this.activeRequests,
      queued: this.queue.length,
      total: this.activeRequests + this.queue.length,
    };
  }

  /**
   * Clear all queued requests
   */
  clear(): void {
    this.queue = [];
  }
}

// Singleton instance
export const requestQueue = new RequestQueue();

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).__requestQueue = requestQueue;
}
